<template>
  <v-container>
    <!-- Export dialog -->
    <ExportRangeDialog
      v-model="showRangeSelection"
      unit="day"
      :title="$filters.capitalize($t('production.export_hint'))"
      :subtitle="exportProducer ? $filters.capitalize(exportProducer.display_name) : ''"
      @update:model-value="onExportDialog"
      @export="download"
    ></ExportRangeDialog>

    <!-- Sticky header: title and controls, then the filters -->
    <div class="topbar">
      <div class="topbar__row">
        <div class="topbar__badge">
          <v-icon size="22">mdi-white-balance-sunny</v-icon>
        </div>
        <div class="topbar__title min-width-0">
          <div class="topbar__name">{{ $filters.capitalize($t("production.section_solar")) }}</div>
          <div class="topbar__sub">
            <span>{{ solar_productions.length }} {{ $t("machine.producers", solar_productions.length) }}</span>
            <span class="topbar__dot"></span>
            <span>{{ stations.length }} {{ $t("production.stations") }}</span>
            <template v-if="lastRefresh">
              <span class="topbar__dot"></span>
              <span>{{ $t("production.last_refresh") }} {{ $filters.formatTime(lastRefresh) }}</span>
            </template>
          </div>
        </div>

        <div class="topbar__actions">
          <v-text-field
            v-model="search"
            class="topbar__search"
            density="compact"
            variant="solo-filled"
            flat
            rounded="lg"
            hide-details
            clearable
            prepend-inner-icon="mdi-magnify"
            :placeholder="$filters.capitalize($t('generic.filter'))"
          ></v-text-field>
          <v-btn
            variant="tonal"
            size="small"
            prepend-icon="mdi-battery-charging-high"
            :to="{ name: 'batteries' }"
          >
            {{ $filters.capitalize($t("production.batteries")) }}
          </v-btn>
          <v-btn
            icon
            variant="tonal"
            color="primary"
            size="small"
            :loading="loading"
            :title="$filters.capitalize($t('production.refresh'))"
            @click="updateData"
          >
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </div>
      </div>

      <div class="topbar__row topbar__row--filters">
        <span class="topbar__label">
          <v-icon size="x-small" class="mr-1">mdi-filter-variant</v-icon>{{ $filters.capitalize($t("generic.filter")) }}
        </span>
        <v-chip
          v-for="f in filters"
          :key="f.key"
          size="small"
          :variant="activeFilters.includes(f.key) ? 'flat' : 'tonal'"
          :color="activeFilters.includes(f.key) ? f.color : undefined"
          :prepend-icon="f.icon"
          class="filter-chip"
          @click="toggleFilter(f.key)"
        >
          {{ $filters.capitalize($t(f.label)) }}
          <span class="filter-chip__count">{{ f.count }}</span>
        </v-chip>
        <v-btn
          v-if="activeFilters.length || search"
          size="x-small"
          variant="text"
          prepend-icon="mdi-close"
          @click="clearFilters"
        >
          {{ $filters.capitalize($t("limitations.reset_filters")) }}
        </v-btn>
        <span class="topbar__result">
          <b>{{ filteredProductions.length }}</b> / {{ solar_productions.length }} {{ $t("machine.producers", solar_productions.length) }}
        </span>
      </div>
      <v-progress-linear v-if="loading" indeterminate color="primary" height="2" class="topbar__progress"></v-progress-linear>
    </div>

    <!-- KPI tiles -->
    <v-row dense class="kpi-row">
      <v-col cols="12" sm="6">
        <v-card flat class="kpi kpi--blue">
          <div class="kpi__badge"><v-icon size="20">mdi-calendar-month</v-icon></div>
          <div class="min-width-0">
            <div class="kpi__label">{{ $filters.capitalize($t("production.label", 1)) }}</div>
            <div class="kpi__value">
              {{ $filters.formatFloat(solar_monthly_cumul / 1000) }}
              <span class="kpi__unit">{{ $t("unity.mwh") }}</span>
            </div>
            <div class="kpi__hint">{{ $t("production.cumulative", solar_monthly_cumul / 1000) }}</div>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6">
        <v-card flat class="kpi kpi--green">
          <div class="kpi__badge"><v-icon size="20">mdi-access-point</v-icon></div>
          <div class="min-width-0">
            <div class="kpi__label">{{ $filters.capitalize($t("production.reporting")) }}</div>
            <div class="kpi__value">
              {{ reportingCount }}
              <span class="kpi__unit">/ {{ solar_productions.length }}</span>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Producers, grouped by power station -->
    <v-card flat class="table-card">

      <!-- Phone: one card per producer, grouped by station -->
      <div class="m-list d-md-none">
        <template v-for="station in stations" :key="`m-${station.id}`">
          <div class="m-station" @click="station.id && openStation(station)">
            <v-icon size="small" color="primary">mdi-factory</v-icon>
            <span class="text-truncate">{{ $filters.capitalize(station.name) }}</span>
            <span class="m-station__stat">{{ $filters.formatFloat(station.power) }} {{ $t("unity.kw") }}</span>
          </div>
          <v-card
            v-for="item in station.items"
            :key="`m-${item.producer.pk}`"
            flat
            class="m-card"
            :class="{ 'm-card--nodata': !item.time }"
            @click="openProducer(item)"
          >
            <span class="m-card__bar" :class="freshnessClass(item.time) || 'fresh'"></span>
            <div class="m-card__body">
              <div class="m-card__head">
                <div class="min-width-0">
                  <div class="m-card__name">{{ $filters.capitalize(item.producer.display_name) }}</div>
                  <div v-if="item.producer.capacity_solar_panel" class="m-card__sub">{{ item.producer.capacity_solar_panel }} {{ $t("unity.kw") }}c</div>
                </div>
                <v-icon v-if="isBelowExpected(item)" size="small" color="warning" class="ml-auto">mdi-alert</v-icon>
              </div>
              <div class="m-card__grid">
                <div>
                  <div class="m-stat__label">{{ $t("telemetry.power") }}</div>
                  <div class="m-stat__value" :class="{ 'text-warning': isBelowExpected(item) }">
                    {{ $filters.formatFloat(item.power) }} <span class="m-stat__unit">{{ $t("unity.kw") }}</span>
                  </div>
                </div>
                <div>
                  <div class="m-stat__label">{{ $t("production.injection") }}</div>
                  <div class="m-stat__value">
                    <template v-if="hasValue(item.injection)">{{ $filters.formatFloat(item.injection) }} <span class="m-stat__unit">{{ $t("unity.kw") }}</span></template>
                    <span v-else class="text-disabled">&ndash;</span>
                  </div>
                </div>
                <div>
                  <div class="m-stat__label">{{ $t("production.irradiance") }}</div>
                  <div class="m-stat__value">
                    <template v-if="hasValue(item.radiance_vert)">{{ $filters.formatFloat(item.radiance_vert) }} <span class="m-stat__unit">{{ $t("unity.wm2") }}</span></template>
                    <span v-else class="text-disabled">&ndash;</span>
                  </div>
                </div>
              </div>
              <div class="m-card__foot">
                <span class="fresh-chip" :class="freshnessClass(item.time) || 'fresh'">
                  <template v-if="item.time">{{ relativeTime(item.time) }}</template>
                  <template v-else>{{ $t("production.no_telemetry") }}</template>
                </span>
                <v-btn
                  icon
                  size="x-small"
                  variant="text"
                  color="primary"
                  class="ml-auto"
                  @click.stop="openRangeSelection(item.producer.pk)"
                >
                  <v-icon size="small">mdi-download</v-icon>
                </v-btn>
              </div>
            </div>
            <v-icon size="small" class="m-card__chevron">mdi-chevron-right</v-icon>
          </v-card>
        </template>
        <div v-if="!stations.length && !loading" class="text-center text-medium-emphasis py-6">
          {{ $filters.capitalize($t("production.no_producer")) }}
        </div>
      </div>

      <v-table class="solar-table d-none d-md-block" density="comfortable">
        <thead>
          <tr>
            <th
              v-for="h in headers"
              :key="h.key"
              :class="[`text-${h.align}`, { sortable: h.sortable, active: sortKey === h.key }]"
              :style="h.width ? { width: h.width } : null"
              @click="h.sortable && setSort(h.key)"
            >
              {{ h.title }}
              <v-icon v-if="h.sortable" size="x-small" class="sort-icon">
                {{ sortKey === h.key && sortDesc ? "mdi-arrow-down" : "mdi-arrow-up" }}
              </v-icon>
            </th>
            <th class="row-chevron"></th>
          </tr>
        </thead>
        <tbody>
          <template v-for="station in stations" :key="station.id">
            <!-- Station header -->
            <tr
              class="station-row"
              :class="{ 'station-row--link': station.id }"
              :title="station.id ? $filters.capitalize($t('machine.open_station')) : null"
              @click="station.id && openStation(station)"
            >
              <td :colspan="headers.length + 1">
                <div class="station-head">
                  <v-btn
                    :icon="isCollapsed(station.id) ? 'mdi-chevron-right' : 'mdi-chevron-down'"
                    size="x-small"
                    variant="text"
                    @click.stop="toggleStation(station.id)"
                  ></v-btn>
                  <v-icon size="small" class="mr-1" color="primary">mdi-factory</v-icon>
                  <span class="station-name">{{ $filters.capitalize(station.name) }}</span>
                  <v-chip size="x-small" variant="tonal" class="ml-2">{{ station.items.length }}</v-chip>
                  <span class="station-stat">
                    <b>{{ $filters.formatFloat(station.power) }}</b> {{ $t("unity.kw") }}
                    <template v-if="station.capacity > 0">
                      <span class="text-medium-emphasis">/ {{ station.capacity }} {{ $t("unity.kw") }}c &middot; {{ Math.round((station.power / station.capacity) * 100) }} %</span>
                    </template>
                  </span>
                  <span class="station-stat text-medium-emphasis">
                    {{ station.reporting }} / {{ station.items.length }} {{ $t("production.reporting") }}
                  </span>
                  <span v-if="station.id" class="station-open">
                    {{ $filters.capitalize($t("machine.open_station")) }}
                    <v-icon size="small">mdi-arrow-right</v-icon>
                  </span>
                </div>
              </td>
            </tr>

            <!-- Producers of the station -->
            <template v-if="!isCollapsed(station.id)">
              <tr
                v-for="item in station.items"
                :key="item.producer.pk"
                class="producer-row producer-row--link"
                :class="{ 'producer-row--nodata': !item.time }"
                :title="$filters.capitalize($t('machine.open_producer'))"
                @click="openProducer(item)"
              >
                <td>
                  <div class="producer-cell">
                    <span
                      class="producer-bar"
                      :class="freshnessClass(item.time) || 'fresh'"
                      :title="item.time ? $filters.formatDateTime(item.time) : $t('production.no_telemetry')"
                    ></span>
                    <div class="min-width-0">
                      <router-link
                        class="producer-name"
                        :to="{ name: 'intervention_reports', query: { producer_id: item.producer.pk } }"
                        @click.stop
                      >
                        {{ $filters.capitalize(item.producer.display_name) }}
                      </router-link>
                      <div v-if="item.producer.capacity_solar_panel" class="text-caption text-medium-emphasis">
                        {{ item.producer.capacity_solar_panel }} {{ $t("unity.kw") }}c
                      </div>
                    </div>
                  </div>
                </td>
                <td class="text-center">
                  <div class="power-cell" :class="{ 'power-cell--warn': isBelowExpected(item), 'power-cell--off': !item.time }">
                    <div class="power-cell__value">
                      <v-icon
                        v-if="isBelowExpected(item)"
                        size="x-small"
                        color="warning"
                        class="mr-1"
                        :title="$filters.capitalize($t('production.below_expected'))"
                      >mdi-alert</v-icon>
                      {{ $filters.formatFloat(item.power) }}
                      <span class="power-cell__unit">{{ $t("unity.kw") }}</span>
                      <span v-if="loadFactor(item) !== null" class="power-cell__pct" :title="$filters.capitalize($t('production.load_factor'))">
                        {{ Math.round(loadFactor(item) * 100) }} %
                      </span>
                    </div>
                    <div class="power-cell__bar" v-if="loadFactor(item) !== null">
                      <div class="power-cell__fill" :style="{ width: `${Math.round(powerBar(item) * 100)}%` }"></div>
                    </div>
                  </div>
                </td>
                <td class="text-center">
                  <span v-if="hasValue(item.injection)">
                    {{ $filters.formatFloat(item.injection) }}
                    <span class="text-caption text-medium-emphasis">{{ $t("unity.kw") }}</span>
                  </span>
                  <span v-else class="text-disabled">&ndash;</span>
                </td>
                <td class="text-center">
                  <span v-if="hasValue(item.radiance_vert)">
                    {{ $filters.formatFloat(item.radiance_vert) }}
                    <span class="text-caption text-medium-emphasis">{{ $t("unity.wm2") }}</span>
                  </span>
                  <span v-else class="text-disabled">&ndash;</span>
                </td>
                <td class="text-center">
                  <span v-if="hasValue(item.radiance_hor)">
                    {{ $filters.formatFloat(item.radiance_hor) }}
                    <span class="text-caption text-medium-emphasis">{{ $t("unity.wm2") }}</span>
                  </span>
                  <span v-else class="text-disabled">&ndash;</span>
                </td>
                <td class="text-center">
                  <template v-if="item.time">
                    <span class="fresh-chip" :class="freshnessClass(item.time) || 'fresh'">
                      {{ relativeTime(item.time) }}
                    </span>
                    <div class="text-caption text-medium-emphasis mt-1">{{ $filters.formatDateTime(item.time) }}</div>
                  </template>
                  <span v-else class="fresh-chip over_5h">{{ $t("production.no_telemetry") }}</span>
                </td>
                <td class="text-center" @click.stop>
                  <div class="row-actions">
                    <span class="row-flags">
                      <v-icon
                        size="small"
                        :class="isBelowExpected(item) ? 'flag--on' : 'flag--off'"
                        :color="isBelowExpected(item) ? 'warning' : undefined"
                        :title="$filters.capitalize($t(isBelowExpected(item) ? 'production.below_expected' : 'production.expected'))"
                      >mdi-speedometer-slow</v-icon>
                    </span>
                    <v-btn
                      icon
                      size="small"
                      variant="text"
                      color="primary"
                      :loading="showSpinner(item.producer.pk)"
                      :title="$filters.capitalize($t('production.export_hint'))"
                      @click.prevent="openRangeSelection(item.producer.pk)"
                    >
                      <v-icon>mdi-download</v-icon>
                    </v-btn>
                  </div>
                </td>
                <td class="row-chevron"><v-icon size="small">mdi-chevron-right</v-icon></td>
              </tr>
            </template>
          </template>
          <tr v-if="!stations.length && !loading">
            <td :colspan="headers.length + 1" class="text-center text-medium-emphasis py-6">
              {{ $filters.capitalize($t("production.no_producer")) }}
            </td>
          </tr>
        </tbody>
      </v-table>
    </v-card>

  </v-container>
</template>

<script>
import moment from "moment";
import { sumValues, isNone } from "@/functions.js";
import utils from "@/mixins/utils";
import ExportRangeDialog from "@/components/ExportRangeDialog.vue";

export default {
  components: { ExportRangeDialog },
  mixins: [utils],

  computed: {
    headers() {
      return [
        { title: this.$filters.capitalize(this.$t("production.name")), key: "name", align: "left", sortable: true, width: "260px" },
        { title: this.$filters.capitalize(this.$t("production.power")), key: "power", align: "center", sortable: true, width: "200px" },
        { title: this.$filters.capitalize(this.$t("production.injection")), key: "injection", align: "center", sortable: true },
        { title: this.$filters.capitalize(this.$t("telemetry.radiance_vertical")), key: "radiance_vert", align: "center", sortable: true },
        { title: this.$filters.capitalize(this.$t("telemetry.radiance_horizontal")), key: "radiance_hor", align: "center", sortable: true },
        { title: this.$filters.capitalize(this.$t("production.last_data")), key: "time", align: "center", sortable: true },
        { title: this.$filters.capitalize(this.$t("actions.label")), key: "actions", align: "center", sortable: false }
      ];
    },

    filters() {
      const all = this.solar_productions;
      return [
        { key: "below", label: "production.below_expected_short", icon: "mdi-speedometer-slow", color: "warning",
          test: (p) => this.isBelowExpected(p), count: all.filter((p) => this.isBelowExpected(p)).length },
        { key: "nodata", label: "production.no_data", icon: "mdi-access-point-off", color: "grey-darken-1",
          test: (p) => isNone(p.time), count: all.filter((p) => isNone(p.time)).length }
      ];
    },

    filteredProductions() {
      const needle = (this.search || "").trim().toLowerCase();
      let items = this.solar_productions;
      this.activeFilters.forEach((key) => {
        const f = this.filters.find((x) => x.key === key);
        if (f) {
          items = items.filter(f.test);
        }
      });
      if (!needle) {
        return items;
      }
      return items.filter(
        (p) =>
          String(p.producer.display_name || "").toLowerCase().includes(needle) ||
          String(p.producer.power_station_name || "").toLowerCase().includes(needle)
      );
    },

    // Producers grouped by power station (stations by name, producers by the chosen column)
    stations() {
      const groups = new Map();
      this.filteredProductions.forEach((p) => {
        const id = p.producer.power_station_id || 0;
        if (!groups.has(id)) {
          groups.set(id, {
            id,
            name: p.producer.power_station_name || this.$t("machine.station"),
            items: [],
            power: 0,
            capacity: 0,
            reporting: 0
          });
        }
        const g = groups.get(id);
        g.items.push(p);
        g.power += Number(p.power) || 0;
        g.capacity += Number(p.producer.capacity_solar_panel) || 0;
        if (!isNone(p.time)) {
          g.reporting += 1;
        }
      });
      const dir = this.sortDesc ? -1 : 1;
      const value = (p) => {
        if (this.sortKey === "name") {
          return String(p.producer.display_name || "").toLowerCase();
        }
        if (this.sortKey === "time") {
          return isNone(p.time) ? null : moment(p.time).valueOf();
        }
        return this.hasValue(p[this.sortKey]) ? Number(p[this.sortKey]) : null;
      };
      const compare = (a, b) => {
        const va = value(a);
        const vb = value(b);
        if (va === null && vb === null) return 0;
        if (va === null) return 1; // empty values always last
        if (vb === null) return -1;
        if (va < vb) return -dir;
        if (va > vb) return dir;
        return 0;
      };
      return Array.from(groups.values())
        .map((g) => ({ ...g, items: [...g.items].sort(compare) }))
        .sort((a, b) => String(a.name).localeCompare(String(b.name)));
    },

    exportProducer() {
      if (isNone(this.producer_id)) {
        return null;
      }
      const prod = this.solar_productions.find((p) => p.producer.pk === this.producer_id);
      return prod ? prod.producer : null;
    },

    reportingCount() {
      return this.solar_productions.filter((p) => !isNone(p.time)).length;
    },

    maxPower() {
      return this.solar_productions.reduce(
        (acc, p) => Math.max(acc, Number(p.power) || 0),
        0
      );
    }
  },

  methods: {
    clearFilters() {
      this.activeFilters = [];
      this.search = "";
    },

    toggleFilter(key) {
      if (this.activeFilters.includes(key)) {
        this.activeFilters = this.activeFilters.filter((k) => k !== key);
      } else {
        this.activeFilters = [...this.activeFilters, key];
      }
    },

    openStation(station) {
      this.$router.push({ name: "machine_solar_producer_view", query: { power_station_id: station.id } });
    },

    openProducer(item) {
      this.$router.push({ name: "solar_producer", query: { producer_id: item.producer.pk } });
    },

    setSort(key) {
      if (this.sortKey === key) {
        this.sortDesc = !this.sortDesc;
      } else {
        this.sortKey = key;
        this.sortDesc = false;
      }
    },

    isCollapsed(stationId) {
      return this.collapsed.includes(stationId);
    },

    toggleStation(stationId) {
      if (this.isCollapsed(stationId)) {
        this.collapsed = this.collapsed.filter((id) => id !== stationId);
      } else {
        this.collapsed = [...this.collapsed, stationId];
      }
    },

    hasValue(value) {
      return !isNone(value) && value !== "";
    },

    // "il y a 12 min", "il y a 3 h", "il y a 2 j" without touching the global moment locale
    relativeTime(value) {
      const minutes = Math.max(0, Math.round(moment().diff(moment(value), "minutes")));
      if (minutes < 60) {
        return this.$t("production.minutes_ago", { n: minutes });
      }
      const hours = Math.round(minutes / 60);
      if (hours < 48) {
        return this.$t("production.hours_ago", { n: hours });
      }
      return this.$t("production.days_ago", { n: Math.round(hours / 24) });
    },

    freshnessClass(last) {
      if (isNone(last)) {
        return "over_5h";
      }
      const last_ = moment(last);
      if (last_ < this.limit_5_hour) {
        return "over_5h";
      } else if (last_ < this.limit_3_hour) {
        return "over_3h";
      } else if (last_ < this.limit_1_hour) {
        return "over_1h";
      }
      return "";
    },

    // Share of the installed capacity currently produced (null when unknown)
    loadFactor(prod) {
      const capacity = Number(prod.producer.capacity_solar_panel);
      if (!capacity || capacity <= 0) {
        return null;
      }
      return Math.max(0, (Number(prod.power) || 0) / capacity);
    },

    // Bar length: load factor when the capacity is known, else relative to the best producer
    powerBar(prod) {
      const lf = this.loadFactor(prod);
      if (lf !== null) {
        return Math.min(1, lf);
      }
      if (this.maxPower <= 0) {
        return 0;
      }
      return Math.min(1, (Number(prod.power) || 0) / this.maxPower);
    },

    isBelowExpected(prod) {
      if (!this.hasValue(prod.performance_ratio_actual_month_min) || isNone(prod.time)) {
        return false;
      }
      return Number(prod.power) < Number(prod.performance_ratio_actual_month_min);
    },

    updateData() {
      this.loading = true;
      this.limit_1_hour = moment().subtract(1, "hours");
      this.limit_3_hour = moment().subtract(3, "hours");
      this.limit_5_hour = moment().subtract(5, "hours");

      const producers = this.$store
        .dispatch(`telemetries/solarCurrent_producerItems`)
        .then((items) => {
          this.solar_productions = items;
        });

      const monthly = this.$store
        .dispatch(`telemetries/solarMonthlyProduction`)
        .then((items) => {
          this.solar_monthly_cumul = sumValues(items);
        });

      Promise.allSettled([producers, monthly]).then(() => {
        this.loading = false;
        this.lastRefresh = moment();
      });
    },

    openRangeSelection(prod_id) {
      this.producer_id = prod_id;
      this.showRangeSelection = true;
    },

    closeRangeSelection() {
      this.showRangeSelection = false;
      this.producer_id = undefined;
    },

    onExportDialog(open) {
      if (!open) {
        this.closeRangeSelection();
      }
    },

    download(range) {
      if (!range || isNone(this.producer_id)) {
        return;
      }
      this.$store.dispatch("onLoading");
      this.getFile("telemetries/solar/producer/export_csv/", "", "text/csv", "blob", {
        start: range.start,
        end: range.end,
        producer_id: this.producer_id
      });
      this.closeRangeSelection();
    },

    showSpinner(prod_id) {
      return this.showRangeSelection && this.producer_id === prod_id;
    }
  },

  mounted() {
    this.updateData();

    this.$nextTick(function() {
      this.intervalId = window.setInterval(
        () => {
          this.updateData();
        },
        10 * 60 * 1000
      );
    });
  },

  unmounted() {
    window.clearInterval(this.intervalId);
  },

  data() {
    return {
      intervalId: undefined,
      loading: false,
      lastRefresh: null,
      search: "",
      activeFilters: [],
      sortKey: "name",
      sortDesc: false,
      collapsed: [],
      solar_productions: [],
      solar_monthly_cumul: 0.0,
      limit_1_hour: moment().subtract(1, "hours"),
      limit_3_hour: moment().subtract(3, "hours"),
      limit_5_hour: moment().subtract(5, "hours"),
      showRangeSelection: false,
      producer_id: undefined
    };
  }
};
</script>

<style scoped>
/* Freshness of the last telemetry: bar and chip share one scale */
.over_1h {
  color: #c9a800;
}
.over_3h {
  color: #e07b00;
}
.over_5h {
  color: #d32f2f;
}
.fresh {
  color: #2e7d32;
}

.fresh-chip {
  /* text in the freshness colour on a 12 % tint of it */
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 500;
  white-space: nowrap;
  background: color-mix(in srgb, currentColor 12%, white);
}

/* Sticky header, pinned under the app bar while the page scrolls */
.topbar {
  position: sticky;
  top: var(--v-layout-top, 0px);
  z-index: 5;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.94);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}
.topbar::before {
  content: "";
  display: block;
  height: 3px;
  background: linear-gradient(90deg, #0093bf 0%, #15a9c9 55%, #f6b73c 100%);
}
.topbar__row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
}
.topbar__row--filters {
  flex-wrap: wrap;
  gap: 6px;
  padding-top: 8px;
  padding-bottom: 8px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  background: #fafbfc;
}
.topbar__badge {
  width: 40px;
  height: 40px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: #fff3d6;
  color: #e08a00;
}
.topbar__title {
  flex: 1 1 200px;
}
.topbar__name {
  font-size: 1.15rem;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
}
.topbar__sub {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.78rem;
  color: rgba(0, 0, 0, 0.6);
  white-space: nowrap;
  overflow: hidden;
}
.topbar__dot {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.3);
  flex-shrink: 0;
}
.topbar__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
}
.topbar__search {
  width: 260px;
}
.topbar__search :deep(.v-field) {
  background: #f1f4f6;
}
.topbar__label {
  display: inline-flex;
  align-items: center;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(0, 0, 0, 0.55);
  margin-right: 4px;
}
.filter-chip {
  cursor: pointer;
}
.filter-chip__count {
  margin-left: 6px;
  padding: 0 6px;
  border-radius: 999px;
  font-size: 0.7rem;
  background: rgba(0, 0, 0, 0.08);
}
.topbar__result {
  margin-left: auto;
  font-size: 0.78rem;
  color: rgba(0, 0, 0, 0.6);
  white-space: nowrap;
}
.topbar__progress {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
}

/* KPI tiles */
.kpi-row {
  margin-top: 10px;
}
.kpi {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  height: 100%;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  transition: box-shadow 0.15s ease, transform 0.15s ease;
}
.kpi:hover {
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);
  transform: translateY(-1px);
}
.kpi__badge {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.kpi--blue .kpi__badge {
  background: #dff3f9;
  color: #0093bf;
}
.kpi--green .kpi__badge {
  background: #e3f4e4;
  color: #2e7d32;
}
.kpi__label {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(0, 0, 0, 0.55);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.kpi__value {
  margin-top: 2px;
  font-size: 1.65rem;
  font-weight: 700;
  line-height: 1.15;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  color: rgba(0, 0, 0, 0.87);
}
.kpi__unit {
  font-size: 0.85rem;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.5);
}
.kpi__hint {
  margin-top: 2px;
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.55);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Producers table */
.table-card {
  margin-top: 10px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  overflow: hidden;
}
.solar-table :deep(th) {
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 0.05em;
  white-space: nowrap;
  color: rgba(0, 0, 0, 0.55) !important;
  background: #fff;
  user-select: none;
}
.solar-table :deep(th.sortable) {
  cursor: pointer;
}
.solar-table :deep(th.sortable:hover),
.solar-table :deep(th.active) {
  color: rgb(var(--v-theme-primary)) !important;
}
.solar-table :deep(th .sort-icon) {
  opacity: 0;
  transition: opacity 0.15s ease;
}
.solar-table :deep(th.sortable:hover .sort-icon) {
  opacity: 0.5;
}
.solar-table :deep(th.active .sort-icon) {
  opacity: 1;
}

/* Station header rows */
.solar-table :deep(tr.station-row td) {
  background: #dfe9ee;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  border-bottom: 1px solid rgba(0, 0, 0, 0.1) !important;
  padding: 4px 8px !important;
}
.solar-table :deep(tr.station-row--link),
.solar-table :deep(tr.producer-row--link) {
  cursor: pointer;
}
.solar-table :deep(tbody tr.producer-row--link:hover td) {
  background-color: #e8f4f9;
}
.station-open {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.4);
  white-space: nowrap;
  transition: color 0.15s ease;
}
tr:hover .station-open {
  color: rgb(var(--v-theme-primary));
}
/* Clickable rows: a chevron at the end that lights up with the row */
.row-chevron {
  width: 36px;
  padding: 0 8px 0 0 !important;
  text-align: right;
}
.row-chevron .v-icon {
  color: rgba(0, 0, 0, 0.26);
  transition: color 0.15s ease, transform 0.15s ease;
}
tr:hover .row-chevron .v-icon {
  color: rgb(var(--v-theme-primary));
  transform: translateX(2px);
}

.solar-table :deep(tr.station-row--link:hover td) {
  background: #d2e0e7;
}
/* Producers without telemetry stand out as a red-tinted line */
.solar-table :deep(tr.producer-row--nodata td) {
  background-color: #fdecec;
}
.solar-table :deep(tr.producer-row--nodata:hover td) {
  background-color: #fadcdc;
}
.solar-table :deep(tr.producer-row--nodata .producer-bar) {
  visibility: hidden;
}
.row-flags {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-right: 10px;
  padding-right: 10px;
  border-right: 1px solid rgba(0, 0, 0, 0.1);
}
.flag--off {
  color: rgba(0, 0, 0, 0.22);
}
.station-head {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
.station-name {
  font-weight: 700;
  font-size: 0.95rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.station-stat {
  margin-left: 18px;
  font-size: 0.85rem;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.solar-table :deep(tr.producer-row td:first-child) {
  padding-left: 28px;
}
.solar-table :deep(td) {
  font-variant-numeric: tabular-nums;
  border-bottom: 1px solid rgba(0, 0, 0, 0.04) !important;
}
.solar-table :deep(tbody tr:hover td) {
  background-color: #f2f9fc;
}
.producer-cell {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.producer-bar {
  /* slim freshness marker, no icon */
  display: inline-block;
  width: 4px;
  height: 30px;
  border-radius: 2px;
  flex-shrink: 0;
  background-color: currentColor;
}
.producer-name {
  font-weight: 600;
  color: rgba(0, 0, 0, 0.87);
  text-decoration: none;
}
.producer-name:hover {
  color: rgb(var(--v-theme-primary));
  text-decoration: underline;
}
.min-width-0 {
  min-width: 0;
}
.power-cell {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  min-width: 140px;
  padding: 4px 0;
  color: #0093bf;
}
.power-cell--warn {
  color: #e08a00;
}
.power-cell--off {
  color: #9e9e9e;
}
.power-cell__value {
  display: inline-flex;
  align-items: baseline;
  font-size: 1.15rem;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.87);
  font-variant-numeric: tabular-nums;
}
.power-cell--off .power-cell__value {
  color: rgba(0, 0, 0, 0.4);
}
.power-cell__unit {
  margin-left: 7px;
  font-size: 0.75rem;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.5);
}
.power-cell__bar {
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background: color-mix(in srgb, currentColor 14%, white);
  overflow: hidden;
}
.power-cell__fill {
  height: 100%;
  border-radius: 3px;
  background: currentColor;
  transition: width 0.3s ease;
}
.power-cell__pct {
  margin-left: 8px;
  padding-left: 8px;
  border-left: 1px solid rgba(0, 0, 0, 0.12);
  font-size: 0.78rem;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.5);
  font-variant-numeric: tabular-nums;
}
.row-actions {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  white-space: nowrap;
}

/* Phone layout: cards instead of the table */
.m-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px;
}
.m-station {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
  padding: 6px 4px 2px;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(0, 0, 0, 0.6);
}
.m-station__stat {
  margin-left: auto;
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0;
  white-space: nowrap;
}
.m-card {
  display: flex;
  align-items: stretch;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  cursor: pointer;
}
.m-card--nodata {
  background: #fdecec;
}
.m-card__bar {
  width: 4px;
  border-radius: 2px;
  background: currentColor;
  flex-shrink: 0;
}
.m-card__body {
  flex: 1;
  min-width: 0;
}
.m-card__head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}
.m-card__name {
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.m-card__sub {
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.55);
}
.m-card__grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 4px 10px;
}
.m-stat__label {
  font-size: 0.66rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(0, 0, 0, 0.5);
  white-space: nowrap;
}
.m-stat__value {
  font-size: 0.95rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.m-stat__unit {
  font-size: 0.7rem;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.5);
}
.m-card__foot {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
}
.m-card__chevron {
  align-self: center;
  color: rgba(0, 0, 0, 0.26);
}

@media (max-width: 600px) {
  /* Tiles readable on a phone: smaller badge, wrapping labels, lighter values */
  .kpi {
    padding: 10px 12px;
    gap: 10px;
  }
  .kpi__label {
    white-space: normal;
    line-height: 1.2;
  }
  .kpi__hint {
    white-space: normal;
    line-height: 1.2;
  }
  .topbar__row {
    flex-wrap: wrap;
  }
  .topbar__actions {
    width: 100%;
    margin-left: 0;
  }
  .topbar__search {
    flex: 1;
    width: auto;
  }
  .topbar__result {
    width: 100%;
    margin-left: 0;
  }
}
</style>
